import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, MessageCircle, XCircle } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { waLink } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {
        /* continua tentando */
      }
      attempts.current += 1;
      if (attempts.current >= 15) {
        setStatus("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-forest px-6">
      <div
        data-testid="payment-success-page"
        className="w-full max-w-md rounded-[2rem] border border-parchment/10 bg-nightcard p-10 text-center shadow-2xl"
      >
        <div className="mb-6 flex justify-center">
          <LogoMark dark size={52} />
        </div>

        {status === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-ochre" />
            <h1 className="mt-5 font-display text-2xl font-medium text-parchment">
              Confirmando seu pagamento...
            </h1>
            <p className="mt-2 text-sm text-parchment/60">Isso leva só alguns segundos.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#25D366]" data-testid="payment-confirmed-icon" />
            <h1 className="mt-5 font-display text-3xl font-medium text-parchment">
              Pagamento confirmado!
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-parchment/65">
              Obrigado pela compra! A equipe Agro Carmo vai entrar em contato para combinar a
              entrega ou retirada.
            </p>
            <a
              href={waLink("Olá, Agro Carmo! Acabei de fazer uma compra pelo site e quero combinar a entrega.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="payment-success-whatsapp"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
            >
              <MessageCircle className="h-4 w-4" />
              Combinar entrega no WhatsApp
            </a>
          </>
        )}

        {(status === "pending" || status === "failed") && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-clay" />
            <h1 className="mt-5 font-display text-2xl font-medium text-parchment">
              {status === "pending" ? "Pagamento em processamento" : "Não encontramos seu pagamento"}
            </h1>
            <p className="mt-2 text-sm text-parchment/60">
              Se o valor foi debitado, fale com a gente que resolvemos na hora.
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="payment-success-home"
          className="mt-6 inline-block text-sm text-parchment/50 transition-colors hover:text-ochre"
        >
          Voltar ao site
        </Link>
      </div>
    </div>
  );
}
